import { img } from "../images";
import type { Blog } from "./types";

const DR_ARUN = "Dr. M. V. Arun Vijaiyan";
const ARUN_BIO =
  "Dr. M. V. Arun Vijaiyan is a Urologist and Andrologist at AV Clinic, Krishnagiri, with an M.S. and M.Ch. (Uro). He focuses on kidney stone care, urinary and prostate health, and male reproductive and sexual health, with a patient-centred approach to diagnosis and treatment.";
const ARUN_ROLE = "Urologist & Andrologist at AV Clinic, Krishnagiri";

export const prostateBlogs: Blog[] = [
  {
    slug: "prostate-problem-signs",
    title: "Common Signs of Prostate Problems",
    category: "Prostate Health",
    excerpt:
      "Prostate symptoms are common with age but should be evaluated rather than dismissed. Learn the signs to watch for.",
    image: img.svcProstateCare,
    imageAlt: "Common signs of prostate problems",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-06-08",
    modified: "2026-06-08",
    readTime: "5 min read",
    popular: true,
    tags: ["prostate", "urinary stream", "prostate health", "urology"],
    primaryKeyword: "prostate problem signs",
    secondaryKeywords: ["prostate symptoms", "prostate problems", "prostate health Krishnagiri"],
    relatedService: "prostate-care",
    relatedServiceLabel: "Prostate Care",
    relatedArticles: ["enlarged-prostate-symptoms", "prostate-health-after-40", "when-to-get-prostate-check"],
    sections: [
      {
        heading: "The prostate and urinary symptoms",
        body: [
          "The prostate is a small gland in men that sits just below the bladder around the urethra. As it changes with age, it can affect how urine flows, which is why prostate problems often show up as urinary symptoms.",
        ],
      },
      {
        heading: "Symptoms to watch for",
        body: ["Signs of a prostate problem commonly include:"],
        list: [
          "Difficulty starting or stopping urination",
          "A weak or interrupted urine stream",
          "Needing to urinate frequently, especially at night",
          "A feeling the bladder is not fully empty",
          "A sudden, strong urge to urinate",
        ],
      },
      {
        heading: "Many causes, not all serious",
        body: [
          "Several conditions can cause similar symptoms, including a benign enlarged prostate, inflammation, infection or, less commonly, prostate cancer. Because the cause is not obvious, proper evaluation is important.",
        ],
      },
      {
        heading: "Evaluating the cause",
        body: [
          "A urologist will assess your symptoms and history and, where appropriate, recommend tests to determine the cause. This is the safe way to address prostate concerns.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do prostate symptoms mean I have cancer?",
        a: "Not necessarily. Most prostate symptoms are due to a benign enlarged prostate or inflammation. Only an evaluation can determine the cause.",
      },
      {
        q: "Are prostate symptoms always treatable?",
        a: "Many symptoms can be significantly improved with the right approach. A urologist will tailor management to your situation.",
      },
    ],
    seo: {
      title: "Common Signs of Prostate Problems | AV Clinic",
      description:
        "Learn the common signs of prostate problems — urinary stream, frequency and urgency — and when to seek urology care in Krishnagiri.",
    },
  },
  {
    slug: "enlarged-prostate-symptoms",
    title: "Enlarged Prostate: Symptoms to Recognise",
    category: "Prostate Health",
    excerpt:
      "An enlarged prostate is common with age. Recognising the symptoms helps you get appropriate care early.",
    image: img.svcBph,
    imageAlt: "Enlarged prostate symptoms",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-06-04",
    modified: "2026-06-04",
    readTime: "5 min read",
    tags: ["enlarged prostate", "BPH", "prostate health", "urology"],
    primaryKeyword: "enlarged prostate symptoms",
    secondaryKeywords: ["enlarged prostate", "BPH symptoms", "prostate enlargement"],
    relatedService: "benign-prostatic-hyperplasia",
    relatedServiceLabel: "BPH Treatment",
    relatedArticles: ["bph-causes-treatment", "night-time-urination", "prostate-problem-signs"],
    sections: [
      {
        heading: "What is an enlarged prostate?",
        body: [
          "An enlarged prostate, medically called benign prostatic hyperplasia (BPH), is a non-cancerous growth of the prostate that commonly occurs as men age. As the gland enlarges, it can press on the urethra and affect urinary flow.",
        ],
      },
      {
        heading: "Symptoms",
        body: ["An enlarged prostate typically causes symptoms such as:"],
        list: [
          "A weak or hesitant urine stream",
          "Difficulty starting to urinate",
          "Frequent urination, especially at night",
          "A feeling of incomplete emptying",
          "A sudden urge to urinate",
        ],
      },
      {
        heading: "Is it serious?",
        body: [
          "BPH is not cancer and is very common. However, the symptoms can be uncomfortable and, if untreated, can lead to complications such as recurrent infections or urinary retention.",
        ],
      },
      {
        heading: "Getting evaluated",
        body: [
          "A proper evaluation confirms the cause of your symptoms and guides the right management, which may include lifestyle changes, medication or, where suitable, specialist procedures.",
        ],
      },
    ],
    faqs: [
      {
        q: "Does an enlarged prostate mean prostate cancer?",
        a: "No. An enlarged prostate is usually benign. Only a proper evaluation can determine the cause of your symptoms.",
      },
      {
        q: "Can an enlarged prostate cause urinary retention?",
        a: "In some cases, yes. Marked blockage can lead to the inability to pass urine, which needs prompt attention.",
      },
    ],
    seo: {
      title: "Enlarged Prostate Symptoms & Care | AV Clinic",
      description:
        "Recognise the symptoms of an enlarged prostate (BPH) and how it is evaluated and managed. Prostate care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "bph-causes-treatment",
    title: "BPH: Causes and Treatment",
    category: "Prostate Health",
    excerpt:
      "Benign prostatic hyperplasia is common with age. Understand what causes it and the treatment options available.",
    image: img.svcBph,
    imageAlt: "BPH causes and treatment options",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-05-30",
    modified: "2026-05-30",
    readTime: "6 min read",
    tags: ["BPH", "prostate enlargement", "urinary symptoms", "urology"],
    primaryKeyword: "BPH treatment",
    secondaryKeywords: ["enlarged prostate treatment", "BPH causes", "benign prostatic hyperplasia"],
    relatedService: "benign-prostatic-hyperplasia",
    relatedServiceLabel: "BPH Treatment",
    relatedArticles: ["enlarged-prostate-symptoms", "prostate-health-after-40", "prostate-health-lifestyle"],
    sections: [
      {
        heading: "Why BPH happens",
        body: [
          "Benign prostatic hyperplasia (BPH) is a non-cancerous enlargement of the prostate that happens as men age. Hormonal changes over time contribute to the gradual growth of prostate tissue.",
          "It is common, affecting a large proportion of men after the age of 50.",
        ],
      },
      {
        heading: "How BPH affects you",
        body: [
          "The enlarged prostate can press on the urethra, causing symptoms such as a weak stream, hesitancy, frequent urination, urgency and night-time voiding.",
        ],
      },
      {
        heading: "Treatment options",
        body: ["Management is tailored to the severity of symptoms and your health:"],
        list: [
          "Lifestyle changes and bladder training",
          "Medication to relax the prostate or shrink it",
          "Minimally invasive procedures in selected cases",
          "Surgical options for severe symptoms",
        ],
      },
      {
        heading: "Choosing the right path",
        body: [
          "Your urologist will discuss the options, their benefits and considerations so you can decide with confidence. Early treatment is usually more comfortable and prevents complications.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is BPH curable?",
        a: "BPH can be very well controlled. Many men get significant relief from symptoms with the right approach.",
      },
      {
        q: "Do I need surgery for BPH?",
        a: "Not necessarily. Mild to moderate symptoms often respond to lifestyle changes and medication. Surgery is considered for severe or refractory symptoms.",
      },
    ],
    seo: {
      title: "BPH Causes & Treatment Options | AV Clinic",
      description:
        "Understand benign prostatic hyperplasia (BPH) — its causes and the treatment options available. Prostate care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "prostate-health-after-40",
    title: "Prostate Health After 40",
    category: "Prostate Health",
    excerpt:
      "Prostate health becomes more relevant after 40. Learn what to watch for and how to care for your prostate.",
    image: img.svcProstateCare,
    imageAlt: "Prostate health after 40",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-05-26",
    modified: "2026-05-26",
    readTime: "5 min read",
    tags: ["prostate after 40", "men's health", "prostate health", "urology"],
    primaryKeyword: "prostate health after 40",
    secondaryKeywords: ["prostate health for men", "men prostate health", "prostate check"],
    relatedService: "prostate-care",
    relatedServiceLabel: "Prostate Care",
    relatedArticles: ["when-to-get-prostate-check", "prostate-health-lifestyle", "bph-causes-treatment"],
    sections: [
      {
        heading: "Why the prostate matters after 40",
        body: [
          "The prostate gland tends to enlarge gradually as men get older. While changes are often benign, they can start to cause symptoms from the 40s and 50s onwards. Being aware of these changes is the first step.",
        ],
      },
      {
        heading: "What to watch for",
        body: ["Pay attention to changes in your urinary habits:"],
        list: [
          "A weaker or slower urine stream",
          "Trouble starting or stopping urination",
          "Needing to urinate more often or at night",
          "A feeling that the bladder is not fully empty",
        ],
      },
      {
        heading: "Healthy habits",
        body: [
          "A balanced diet, regular exercise, a healthy weight and staying hydrated all support prostate and overall health. Some evidence links an active lifestyle and good diet to better prostate health.",
        ],
      },
      {
        heading: "When to have a check",
        body: [
          "If you notice symptoms, or if you have a family history of prostate problems, a urology assessment is worthwhile. Your doctor can advise on when a prostate check is appropriate for you.",
        ],
      },
    ],
    faqs: [
      {
        q: "At what age should men start prostate screening?",
        a: "This varies and is a personal decision. Many men discuss it with their doctor from around age 40 to 50, especially with risk factors. Your doctor can guide you.",
      },
      {
        q: "Does an active lifestyle improve prostate health?",
        a: "Regular activity and a healthy weight support general health and may support prostate health. A balanced approach is recommended.",
      },
    ],
    seo: {
      title: "Prostate Health After 40: What Men Should Know | AV Clinic",
      description:
        "Prostate health guidance for men over 40 — symptoms to watch, healthy habits and when to get a check in Krishnagiri. From AV Clinic.",
    },
  },
  {
    slug: "night-time-urination",
    title: "Frequent Urination at Night: Causes and Relief",
    category: "Prostate Health",
    excerpt:
      "Waking repeatedly to urinate disrupts sleep and energy. Understand nocturia and how it can be relieved.",
    image: img.svcMaleUrinary,
    imageAlt: "Frequent urination at night causes",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-05-22",
    modified: "2026-05-22",
    readTime: "5 min read",
    tags: ["night urination", "nocturia", "prostate", "sleep"],
    primaryKeyword: "frequent urination at night",
    secondaryKeywords: ["nocturia", "waking to urinate", "night urination causes"],
    relatedService: "prostate-care",
    relatedServiceLabel: "Prostate Care",
    relatedArticles: ["frequent-urination", "prostate-problem-signs", "bladder-health-tips"],
    sections: [
      {
        heading: "What is nocturia?",
        body: [
          "Nocturia is the need to get up one or more times during the night to urinate. Everyone differs, but repeatedly waking to urinate can seriously disrupt sleep and daytime energy.",
        ],
      },
      {
        heading: "Common causes",
        body: ["Night-time urination can be caused by:"],
        list: [
          "Drinking large amounts of fluid late in the evening",
          "Prostate enlargement in men",
          "An overactive bladder",
          "Urinary infection",
          "Certain medical conditions",
        ],
      },
      {
        heading: "Simple steps that help",
        body: ["Before anything else, try these habits:"],
        list: [
          "Reduce fluids in the two to three hours before bed",
          "Limit caffeine and alcohol, especially in the evening",
          "Avoid diuretic drinks late in the day",
          "Empty your bladder fully before sleeping",
        ],
      },
      {
        heading: "When to seek care",
        body: [
          "If night-time urination persists, or comes with other urinary symptoms, a urological evaluation can identify the cause and guide appropriate management.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is waking once at night normal?",
        a: "An occasional episode is usually fine, but frequent or worsening nocturia deserves evaluation.",
      },
      {
        q: "Does an enlarged prostate cause nighttime urination?",
        a: "Yes. Prostate enlargement is a common cause of increased night-time urination in men.",
      },
    ],
    seo: {
      title: "Frequent Urination at Night: Causes & Relief | AV Clinic",
      description:
        "Learn the causes of frequent night-time urination and simple steps for relief. Prostate and bladder care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "psa-testing",
    title: "Understanding PSA Testing",
    category: "Prostate Health",
    excerpt:
      "The PSA blood test is a common prostate check. Understand what it measures, how it is used and what results mean.",
    image: img.svcUroConsult,
    imageAlt: "Understanding PSA testing for prostate health",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-05-18",
    modified: "2026-05-18",
    readTime: "5 min read",
    tags: ["PSA test", "prostate", "screening", "urology"],
    primaryKeyword: "PSA testing",
    secondaryKeywords: ["PSA test", "prostate specific antigen", "PSA levels"],
    relatedService: "prostate-care",
    relatedServiceLabel: "Prostate Care",
    relatedArticles: ["when-to-get-prostate-check", "prostate-cancer-warning-signs", "prostate-health-after-40"],
    sections: [
      {
        heading: "What is PSA?",
        body: [
          "Prostate-specific antigen (PSA) is a protein produced by the prostate. A blood test measures its level. PSA is a helpful tool in assessing prostate health, but it is not a diagnosis on its own.",
        ],
      },
      {
        heading: "Why PSA levels vary",
        body: ["Several factors can raise PSA, including:"],
        list: [
          "Prostate enlargement",
          "Inflammation or infection of the prostate",
          "Recent procedures or vigorous activity",
          "In some cases, prostate cancer",
        ],
      },
      {
        heading: "How the test is used",
        body: [
          "PSA is used alongside other information, such as your symptoms and a prostate examination, to decide whether further evaluation is needed. A raised PSA does not automatically mean cancer.",
        ],
      },
      {
        heading: "Making a decision",
        body: [
          "Whether to have a PSA test is a personal decision best made with your doctor, considering your age, symptoms and family history. Your doctor will explain the benefits and limitations.",
        ],
      },
    ],
    faqs: [
      {
        q: "Does a high PSA mean I have prostate cancer?",
        a: "No. A high PSA can be caused by many benign conditions. It signals the need for further evaluation.",
      },
      {
        q: "How often should men have a PSA test?",
        a: "This depends on your risk and is best decided with your doctor. Men with risk factors may be advised to test earlier.",
      },
    ],
    seo: {
      title: "Understanding PSA Testing for Prostate Health | AV Clinic",
      description:
        "Learn what PSA testing measures, why levels vary and what results mean. Prostate health guidance at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "prostate-cancer-warning-signs",
    title: "Prostate Cancer: Warning Signs to Know",
    category: "Prostate Health",
    excerpt:
      "Early prostate cancer often has no symptoms. Learn the warning signs and the value of early evaluation.",
    image: img.svcUroConsult,
    imageAlt: "Prostate cancer warning signs",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-05-14",
    modified: "2026-05-14",
    readTime: "5 min read",
    tags: ["prostate cancer", "PSA", "warning signs", "urology"],
    primaryKeyword: "prostate cancer warning signs",
    secondaryKeywords: ["prostate cancer symptoms", "PSA screening", "prostate health"],
    relatedService: "prostate-care",
    relatedServiceLabel: "Prostate Care",
    relatedArticles: ["psa-testing", "prostate-problem-signs", "when-to-get-prostate-check"],
    sections: [
      {
        heading: "Why early detection matters",
        body: [
          "Prostate cancer often develops slowly and may cause no symptoms in its early stages. This is why evaluation and, where appropriate, screening are discussed with doctors, especially for men at higher risk.",
        ],
      },
      {
        heading: "Symptoms that can occur",
        body: ["When symptoms appear, they may be similar to other prostate conditions:"],
        list: [
          "Difficulty or pain when urinating",
          "A weak or interrupted stream",
          "Blood in the urine or semen",
          "Pelvic or lower back discomfort",
        ],
      },
      {
        heading: "Risk factors",
        body: [
          "Risk factors include increasing age, a family history of prostate or breast cancer, and certain genetic factors. Men with these factors may benefit from earlier discussion with a urologist.",
        ],
      },
      {
        heading: "The value of a check-up",
        body: [
          "Because symptoms overlap with common benign conditions, a proper evaluation — which may include a PSA test and examination — is the best way to understand what is happening.",
        ],
      },
    ],
    faqs: [
      {
        q: "Does prostate cancer always cause symptoms?",
        a: "No. Early prostate cancer often causes no symptoms, which is why evaluation matters.",
      },
      {
        q: "Who should discuss prostate screening?",
        a: "Men with symptoms, a family history or risk factors should discuss screening with a doctor. It is a personal decision.",
      },
    ],
    seo: {
      title: "Prostate Cancer: Warning Signs & Early Detection | AV Clinic",
      description:
        "Understand prostate cancer warning signs and the importance of early evaluation. Prostate health advice from AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "prostatitis",
    title: "Prostatitis: Symptoms and Causes",
    category: "Prostate Health",
    excerpt:
      "Prostatitis is inflammation of the prostate and is common in men of all ages. Learn its symptoms and causes.",
    image: img.svcProstateCare,
    imageAlt: "Prostatitis symptoms and causes",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-05-10",
    modified: "2026-05-10",
    readTime: "5 min read",
    tags: ["prostatitis", "prostate inflammation", "pelvic pain", "urology"],
    primaryKeyword: "prostatitis symptoms",
    secondaryKeywords: ["prostatitis", "prostate inflammation", "pelvic pain men"],
    relatedService: "prostate-care",
    relatedServiceLabel: "Prostate Care",
    relatedArticles: ["prostate-problem-signs", "painful-urination", "uti-symptoms"],
    sections: [
      {
        heading: "What is prostatitis?",
        body: [
          "Prostatitis is inflammation or infection of the prostate gland. It is common and can affect men of any age, and it does not necessarily mean prostate cancer or a bad outcome.",
        ],
      },
      {
        heading: "Symptoms",
        body: ["Symptoms of prostatitis can include:"],
        list: [
          "Pain or discomfort in the pelvis, lower back or perineum",
          "Burning or painful urination",
          "A frequent or urgent need to urinate",
          "Pain with ejaculation, in some men",
          "Fever, in acute cases",
        ],
      },
      {
        heading: "Causes",
        body: [
          "Prostatitis can be caused by a bacterial infection or be non-bacterial, involving inflammation without a clear infection. Chronic prostatitis can be persistent and frustrating, but it is manageable.",
        ],
      },
      {
        heading: "Getting the right care",
        body: [
          "Because symptoms overlap with other prostate and urinary conditions, proper evaluation is important. A urologist can diagnose the type of prostatitis and guide effective management.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is prostatitis contagious?",
        a: "No. Prostatitis is not contagious and cannot be passed to a partner.",
      },
      {
        q: "Can prostatitis be cured?",
        a: "Bacterial prostatitis often responds to treatment. Chronic prostatitis may require longer-term management to control symptoms.",
      },
    ],
    seo: {
      title: "Prostatitis: Symptoms, Causes & Care | AV Clinic",
      description:
        "Learn about prostatitis, its symptoms and causes, and how a urologist can help. Prostate care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "prostate-health-lifestyle",
    title: "Prostate Health and Lifestyle",
    category: "Prostate Health",
    excerpt:
      "Daily habits can support prostate health. Learn the lifestyle choices that help maintain a healthy prostate.",
    image: img.svcMensHealth,
    imageAlt: "Lifestyle habits for prostate health",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-05-06",
    modified: "2026-05-06",
    readTime: "5 min read",
    tags: ["prostate health", "diet", "exercise", "men's health"],
    primaryKeyword: "prostate health lifestyle",
    secondaryKeywords: ["prostate diet", "lifestyle prostate health", "men health habits"],
    relatedService: "prostate-care",
    relatedServiceLabel: "Prostate Care",
    relatedArticles: ["prostate-health-after-40", "exercise-mens-health", "healthy-lifestyle-adults"],
    sections: [
      {
        heading: "Lifestyle supports prostate health",
        body: [
          "While no single habit guarantees prostate health, a healthy lifestyle supports overall wellbeing and may help maintain prostate function as you age.",
        ],
      },
      {
        heading: "What to focus on",
        body: ["These habits are worthwhile for prostate and general health:"],
        list: [
          "A balanced diet rich in vegetables, fruit and healthy fats",
          "Regular physical activity and a healthy weight",
          "Limiting red and processed meat, and excess fat",
          "Staying hydrated",
          "Avoiding smoking and limiting alcohol",
        ],
      },
      {
        heading: "Stay proactive",
        body: [
          "Being proactive about prostate health means staying aware of changes in your urinary habits and getting evaluated when something feels different.",
        ],
      },
      {
        heading: "The bigger picture",
        body: [
          "Lifestyle choices also protect your heart, kidneys and general health. A holistic approach to health is the most sensible way forward.",
        ],
      },
    ],
    faqs: [
      {
        q: "Does diet affect prostate health?",
        a: "A balanced diet supports general health and may support prostate health. There is no single food that guarantees protection, so a varied diet is best.",
      },
      {
        q: "Does exercise help the prostate?",
        a: "Regular activity supports overall health and a healthy weight, which are beneficial for prostate and general wellbeing.",
      },
    ],
    seo: {
      title: "Prostate Health & Lifestyle Habits | AV Clinic",
      description:
        "Learn the lifestyle habits that support prostate health — diet, exercise and staying proactive. Men's health guidance at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "when-to-get-prostate-check",
    title: "When Should Men Get a Prostate Check?",
    category: "Prostate Health",
    excerpt:
      "Prostate checks are a personal decision. Learn when a prostate evaluation is wise and what it involves.",
    image: img.svcUroConsult,
    imageAlt: "When men should get a prostate check",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-05-02",
    modified: "2026-05-02",
    readTime: "5 min read",
    tags: ["prostate check", "PSA", "screening", "urology"],
    primaryKeyword: "when to get a prostate check",
    secondaryKeywords: ["prostate examination", "PSA screening", "prostate check up"],
    relatedService: "prostate-care",
    relatedServiceLabel: "Prostate Care",
    relatedArticles: ["prostate-health-after-40", "psa-testing", "prostate-problem-signs"],
    sections: [
      {
        heading: "Prostate checks are individual",
        body: [
          "There is no single age at which every man must have a prostate check. The decision depends on your age, symptoms, family history and personal preference, discussed with a doctor.",
        ],
      },
      {
        heading: "When a check is wise",
        body: ["Consider a prostate evaluation if:"],
        list: [
          "You notice urinary symptoms such as a weak stream or frequent voiding",
          "You have a family history of prostate problems",
          "You are in your 40s or 50s and want to discuss screening",
          "You have blood in the urine or unexplained pelvic discomfort",
        ],
      },
      {
        heading: "What a prostate check involves",
        body: [
          "A prostate check usually starts with a discussion of symptoms and may include a PSA blood test and a specialist examination. These simple steps provide valuable information.",
        ],
      },
      {
        heading: "No reason to delay",
        body: [
          "Most prostate conditions are manageable, especially when found early. If you have concerns, a confidential consultation is the right place to start.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is a prostate check painful?",
        a: "A prostate exam can be briefly uncomfortable but is usually quick and not painful. Discussing it with a urologist helps you feel at ease.",
      },
      {
        q: "Should I have a prostate check if I have no symptoms?",
        a: "Some men choose to discuss screening with their doctor based on age and risk. It is a personal decision.",
      },
    ],
    seo: {
      title: "When Should Men Get a Prostate Check? | AV Clinic",
      description:
        "Learn when a prostate check is wise in Krishnagiri — symptoms, family history and age. Prostate evaluation at AV Clinic.",
    },
  },
];